import { RefObject, useLayoutEffect } from 'react';

/** The space between the bar and the last line of the page. */
const CONSENT_BAR_GAP = 12;

/** The CSS custom property that holds the space of the bar (0px while it is closed). */
export const CONSENT_BAR_SPACE_PROPERTY = '--m24-consent-bar-space';

/**
 * The space a fixed bar takes at the foot of the screen: from its top edge to the bottom of the
 * window, plus a gap. Pure, so the tests need no layout.
 */
export function consentBarSpace(barTop: number, viewportHeight: number): number {
  return Math.max(0, Math.round(viewportHeight - barTop + CONSENT_BAR_GAP));
}

/**
 * While the consent bar shows, the page gets bottom padding as tall as the bar, so the bar never
 * covers the end of a page (onboarding review 2026-10-19). Consent is never implied, so the bar
 * stays until a choice; it only makes room. The space also goes into `--m24-consent-bar-space` for
 * a page that scrolls inside its own panel.
 */
export function useConsentBarSpace(barRef: RefObject<HTMLElement | null>, isShown: boolean): void {
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!isShown || bar === null) {
      return undefined;
    }
    const { body, documentElement } = document;
    // The padding this hook set last. The cleanup removes only that value, so a second run of the
    // effect can never take our own padding for the padding of the page.
    let applied: string | null = null;
    const apply = (): void => {
      const space = `${consentBarSpace(bar.getBoundingClientRect().top, window.innerHeight)}px`;
      body.style.paddingBottom = space;
      applied = space;
      documentElement.style.setProperty(CONSENT_BAR_SPACE_PROPERTY, space);
    };
    apply();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    observer?.observe(bar);
    window.addEventListener('resize', apply);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', apply);
      if (applied !== null && body.style.paddingBottom === applied) {
        body.style.removeProperty('padding-bottom');
      }
      documentElement.style.removeProperty(CONSENT_BAR_SPACE_PROPERTY);
    };
  }, [barRef, isShown]);
}
